import { useState, type FormEvent } from 'react';
import { useAuth, useUser } from '../../auth/AuthProvider';
import { COMPETENCIES, WORK_TYPES } from '../../data/domain';
import { useTechnicians } from '../../data/hooks';
import type { Technician, User } from '../../data/types';
import { dateKey, displayDate, uid } from '../../lib/format';
import { downloadBlob, loadFile } from '../../sync/files';
import { deleteEntity, putEntity, queueFile } from '../../sync/outbox';
import { Modal } from '../../ui/Modal';
import { Icon } from '../../ui/Icon';
import { EmptyState, PageHeading, View } from '../../ui/View';
import { createAccount, findTechnicianAccount, updateAccount, useAccounts } from '../users/usersApi';

export function TechniciansView() {
  const user = useUser();
  const technicians = useTechnicians();
  const { accounts, reload } = useAccounts(user.role === 'admin');
  const [editing, setEditing] = useState<Technician | 'new' | null>(null);
  const today = dateKey();
  return (
    <View id="technicians">
      <PageHeading eyebrow="Operations" title="Technician register" subtitle="Maintain contact records, work arrangements, licences and current competencies.">
        <button className="btn btn-primary" id="addTechnician" onClick={() => setEditing('new')}><Icon name="plus" size={16} />Add technician</button>
      </PageHeading>
      <div className="panel">
        <div className="table-head technician-columns" aria-hidden="true"><span>Technician</span><span>Contact</span><span>Licence</span><span>Competencies</span><span /></div>
        <div className="row-list" id="technicianList">
          {technicians.length ? technicians.map((tech) => {
            const expired = Boolean(tech.expiry && tech.expiry < today);
            return (
              <article className="row technician-columns" key={tech.id}>
                <span className="row-text"><b>{tech.name}</b><small>{tech.workType}{tech.email ? ` · ${tech.email}` : ''}</small></span>
                <span className="row-text"><span>{tech.phone || 'No phone recorded'}</span><small>{tech.emergencyName ? `Emergency: ${tech.emergencyName}` : 'No emergency contact'}</small></span>
                <span className="row-text"><span>{tech.licence || 'No licence recorded'}</span>{tech.expiry ? <small className={expired ? 'text-danger' : ''}>{expired ? 'Expired' : 'Expires'} {displayDate(tech.expiry)}</small> : <small>No expiry recorded</small>}</span>
                <small className="row-meta">{tech.competencies.length ? `${tech.competencies.slice(0, 3).join(', ')}${tech.competencies.length > 3 ? ` +${tech.competencies.length - 3}` : ''}` : 'None recorded'}</small>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(tech)}>Open record</button>
              </article>
            );
          }) : <EmptyState>No technicians recorded yet.</EmptyState>}
        </div>
      </div>
      {editing && <TechnicianModal technician={editing === 'new' ? null : editing} accounts={accounts} canManageAccounts={user.role === 'admin'} onAccountsChanged={reload} onClose={() => setEditing(null)} />}
    </View>
  );
}

const suggestedUsername = (name: string) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '.').replace(/^\.|\.$/g, '');

function TechnicianModal({ technician, accounts, canManageAccounts, onAccountsChanged, onClose }: { technician: Technician | null; accounts: User[]; canManageAccounts: boolean; onAccountsChanged(): void; onClose(): void }) {
  const { config } = useAuth();
  // With username/password sign-in off, a technician's account is an invitation for their email address.
  const localLogin = config?.localLogin ?? true;
  const [saved, setSaved] = useState<Technician | null>(technician);
  const [form, setForm] = useState({ name: technician?.name ?? '', workType: technician?.workType ?? 'Employee', phone: technician?.phone ?? '', email: technician?.email ?? '', emergencyName: technician?.emergencyName ?? '', emergencyPhone: technician?.emergencyPhone ?? '', licence: technician?.licence ?? '', expiry: technician?.expiry ?? '' });
  const [competencies, setCompetencies] = useState<string[]>(technician?.competencies ?? []);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const account = saved ? findTechnicianAccount(saved, accounts) : undefined;
  const [createLogin, setCreateLogin] = useState(false);
  const [username, setUsername] = useState(suggestedUsername(technician?.name ?? ''));
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState(account ? `Account status: ${account.active ? 'active' : 'access removed'}.` : '');
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    if (key === 'name' && !usernameTouched && !account) setUsername(suggestedUsername(event.target.value));
  };

  async function save(event: FormEvent) {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) return setMessage('Enter the technician’s full name.');
    const id = saved?.id ?? uid('technician-');
    let contract = saved?.contract ?? null;
    if (contractFile) {
      const fileId = await queueFile(contractFile, { namespace: 'technicians', key: id, name: contractFile.name, type: contractFile.type || 'application/octet-stream' }, `Contract for ${name}`);
      contract = { fileId, name: contractFile.name };
    }
    const record: Technician = { id, name, workType: form.workType, phone: form.phone.trim(), email: form.email.trim(), contract, contractName: contract?.name ?? saved?.contractName ?? '', emergencyName: form.emergencyName.trim(), emergencyPhone: form.emergencyPhone.trim(), licence: form.licence.trim(), expiry: form.expiry, competencies };
    // The technician record always saves (offline too); creating a login is a separate, optional step.
    await putEntity('technicians', record, `${saved ? 'Updated' : 'New'} technician ${name}`);
    setSaved(record);
    setContractFile(null);
    if (!canManageAccounts) return onClose();
    if (createLogin && !account) {
      if (localLogin && (!username.trim() || password.length < 12)) return setMessage('Technician saved. Enter a username and a temporary password of at least 12 characters to create their account.');
      if (!localLogin && !record.email) return setMessage('Technician saved. Add their email address to invite them to sign in.');
      try {
        await createAccount(localLogin
          ? { displayName: record.name, username: username.trim(), email: config?.oidc ? record.email : '', password, role: 'tech', active: true, technicianEmail: record.email, technicianId: record.id }
          : { displayName: record.name, username: '', email: record.email, password: '', role: 'tech', active: true, technicianEmail: record.email, technicianId: record.id });
        onAccountsChanged();
      } catch (failure) {
        return setMessage(`Technician saved. Account was not created: ${(failure as Error).message || 'try again.'}`);
      }
    } else {
      const unlinked = findTechnicianAccount(record, accounts);
      if (unlinked && !unlinked.technicianId) updateAccount(unlinked, { technicianId: record.id }).then(onAccountsChanged).catch(() => undefined);
    }
    onClose();
  }

  async function remove() {
    if (!saved || !window.confirm('Delete this technician record?')) return;
    await deleteEntity('technicians', saved.id, `Deleted technician ${saved.name}`);
    onClose();
  }

  async function downloadContract() {
    if (!saved?.contract) return;
    const blob = await loadFile({ kind: 'file', fileId: saved.contract.fileId });
    if (blob) downloadBlob(blob, saved.contract.name); else window.alert('The contract downloads once this device is back online.');
  }

  return (
    <Modal id="technicianModal" size="lg" onClose={onClose} labelledBy="technicianModalTitle">
      <form onSubmit={save}>
        <p className="eyebrow">Technician record</p>
        <h2 id="technicianModalTitle">{saved ? 'Edit technician' : 'Add technician'}</h2>
        <div className="form-row">
          <label>Full name <input id="techName" placeholder="Full name" value={form.name} onChange={set('name')} autoFocus={!saved} /></label>
          <label>Work type <select id="techWorkType" value={form.workType} onChange={set('workType')}>{WORK_TYPES.map((type) => <option key={type}>{type}</option>)}</select></label>
        </div>
        <div className="form-row">
          <label>Phone <input id="techPhone" placeholder="04xx xxx xxx" value={form.phone} onChange={set('phone')} /></label>
          <label>Email <input id="techEmail" type="email" placeholder="name@example.com" value={form.email} onChange={set('email')} /></label>
        </div>
        <label>Contract document <input id="techContract" type="file" accept=".pdf,.doc,.docx" onChange={(event) => setContractFile(event.target.files?.[0] ?? null)} /></label>
        {saved?.contract ? <p className="hint">Current contract: <button type="button" className="btn-link" onClick={downloadContract}>{saved.contract.name}</button></p> : saved?.contractName ? <p className="hint">Contract on file: {saved.contractName}</p> : null}
        <div className="form-row">
          <label>Emergency contact <input id="techEmergencyName" placeholder="Name" value={form.emergencyName} onChange={set('emergencyName')} /></label>
          <label>Emergency phone <input id="techEmergencyPhone" placeholder="Phone" value={form.emergencyPhone} onChange={set('emergencyPhone')} /></label>
        </div>
        <div className="form-row">
          <label>Pest management licence number <input id="techLicence" placeholder="Licence number" value={form.licence} onChange={set('licence')} /></label>
          <label>Licence expiry <input id="techLicenceExpiry" type="date" value={form.expiry} onChange={set('expiry')} /></label>
        </div>
        <fieldset className="check-group check-group-wide">
          <legend>Queensland licence competencies / authorisations</legend>
          {COMPETENCIES.map((item) => <label className="check" key={item}><input type="checkbox" checked={competencies.includes(item)} onChange={(event) => setCompetencies((current) => (event.target.checked ? [...current, item] : current.filter((value) => value !== item)))} /> {item}</label>)}
        </fieldset>
        {canManageAccounts && !account && <label className="check"><input id="createTechAccount" type="checkbox" checked={createLogin} onChange={(event) => setCreateLogin(event.target.checked)} /> Create a technician account with field access</label>}
        {canManageAccounts && (createLogin || account) && !localLogin && (
          <p className="form-help" id="techAccountSignIn">{account ? `Signs in as ${account.email || account.username}.` : form.email.trim() ? `They will sign in with ${config?.providerName || 'their account'} using ${form.email.trim()}.` : 'Add their email address above; they sign in with it.'}</p>
        )}
        {canManageAccounts && (createLogin || account) && localLogin && (
          <div className="form-row tech-account-fields">
            <label>Username <input id="techAccountUsername" placeholder="e.g. j.smith" value={account?.username ?? username} readOnly={Boolean(account)} onChange={(event) => { setUsername(event.target.value); setUsernameTouched(true); }} /></label>
            {!account && <label>Temporary password <input id="techAccountPassword" type="password" autoComplete="new-password" placeholder="At least 12 characters" value={password} onChange={(event) => setPassword(event.target.value)} /></label>}
          </div>
        )}
        <p className="form-help" id="techAccountMessage" aria-live="polite">{message}</p>
        <div className="form-actions">
          <button className="btn btn-primary" id="saveTechnician" type="submit">Save technician</button>
          {saved && <button className="btn btn-danger" id="deleteTechnician" type="button" onClick={remove}>Delete</button>}
        </div>
      </form>
    </Modal>
  );
}
